import { useState, useRef, useMemo } from "react";
import { useQueryClient, useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Seller, getMarketplaceId, getSellers } from "@api/apiClient/sellers";
// import AddSeller from "@components/AddSeller";
import { H4, Title2, SubTitle3 } from "@components/new/Typo";
import { Modal, ModalSize } from "@components/new/Modal";
import { MessageModalContent } from "@components/modals/MessageModalNew";
import { Marketplace } from "@components/new/Marketplace";
import WBForm from "./WBForm";
import YMForm from "./YMForm";
import AliForm from "./AliForm";
import OzonForm from "./OzonForm";
import { FormHandle } from "./types";
import { useProfile } from "@contexts/ProfileContext";
import styles from "./EditShopModal.module.scss";

interface EditShopModalProps {
  onClose: () => void;
  seller: Seller;
}

interface ISubmitButtonProps {
  disabled: boolean;
  isLoading?: boolean;
}

enum steps {
  FORM = "form",
  SUCCESS = "success",
  ERROR = "error",
}

const EditShopModal = ({ onClose, seller }: EditShopModalProps) => {
  const [step, setStep] = useState(steps.FORM);
  const [buttonProps, setButtonProps] = useState<ISubmitButtonProps>({
    disabled: false,
    isLoading: false,
  });
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const formRef = useRef<FormHandle>({ submit: () => { } });
  const marketplaceId = getMarketplaceId(seller);

  const { account } = useProfile();

  const { data: sellersData } = useQuery({
    queryKey: ["sellers"],
    queryFn: () => getSellers(),
    retry: 1,
    enabled: false,
  });

  const knownEmails = useMemo(() => {
    return [
      account?.email ?? "",
      ...(sellersData?.items || []).map((v: Seller) => v.email ?? ""),
    ];
  }, [sellersData?.items, account?.email]);

  const handleButtonProps = (props: ISubmitButtonProps) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      isLoading: props.isLoading ?? false,
    });
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["sellers"] });
    queryClient.invalidateQueries({ queryKey: ["parcels-page"] });
    queryClient.invalidateQueries({ queryKey: ["returns-page"] });
    setStep(steps.SUCCESS);
  };

  const onError = () => {
    setStep(steps.ERROR);
  };

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={t("storesPage.editShopModal.title")}
        onClose={onClose}
        size={ModalSize.M}
        footerButtons={[
          {
            title: t("storesPage.editShopModal.successBtn"),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          title={t(["Wildberries", "Aliexpress"].includes(marketplaceId) ? "storesPage.editShopModal.successTitleWb" : "storesPage.editShopModal.successTitle")}
          description={t(["Wildberries", "Aliexpress"].includes(marketplaceId) ? "storesPage.editShopModal.successDescriptionWb" : "storesPage.editShopModal.successDescription")}
          variant="success"
        />
      </Modal>
    );
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={t("storesPage.editShopModal.title")}
        onClose={onClose}
        size={ModalSize.M}
        footerButtons={[
          {
            title: t("storesPage.editShopModal.errorCancelBtn"),
            onClick: onClose,
          },
          {
            title: t("storesPage.editShopModal.errorRepeateBtn"),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t("storesPage.editShopModal.errorTitle")}
          description={t("storesPage.editShopModal.errorDescription")}
          variant="warning"
        />
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={true}
      title={t("storesPage.editShopModal.title")}
      size={ModalSize.M}
      onClose={onClose}
      confirmCancel
      footerButtons={[
        {
          title: formRef.current?.back
            ? t("storesPage.editShopModal.backBtn")
            : t("storesPage.editShopModal.cancelBtn"),
          onClick: formRef.current?.back,
        },
        {
          title: t("storesPage.editShopModal.saveBtn"),
          onClick: () => {
            formRef.current && formRef.current.submit();
          },
          disabled: buttonProps.disabled,
          loading: buttonProps.isLoading,
        },
      ]}
    >
      <div className={styles.content}>
        <div className={styles.head}>
          {["Wildberries", "Aliexpress"].includes(marketplaceId) ? (
            <>
              <div className={styles.headRow}>
                <H4>{seller.name}</H4>
                <div className={styles.marketplaceRow}>
                  <Marketplace name={marketplaceId} size="small" />
                  <Title2>{t(`marketplace.${marketplaceId}`)}</Title2>
                </div>
              </div>
              <SubTitle3 color="grey-800">{seller.email ?? ""}</SubTitle3>
            </>
          ) : (
            <>
              <H4>{seller.name}</H4>
              <div className={styles.marketplaceRow}>
                <Marketplace name={marketplaceId} size="small" />
                <Title2>{t(`marketplace.${marketplaceId}`)}</Title2>
              </div>
            </>
          )}
        </div>
        {marketplaceId === "Ozon" ? (
          <OzonForm
            formRef={formRef}
            onStatusChange={handleButtonProps}
            onSuccess={onSuccess}
            onError={onError}
            seller={seller}
          />
        ) : null}
        {marketplaceId === "Yandex" ? (
          <YMForm
            formRef={formRef}
            onStatusChange={handleButtonProps}
            onSuccess={onSuccess}
            onError={onError}
            seller={seller}
            knownEmails={knownEmails}
          />
        ) : null}
        {marketplaceId === "Wildberries" ? (
          <WBForm
            formRef={formRef}
            onStatusChange={handleButtonProps}
            onSuccess={onSuccess}
            onError={onError}
            seller={seller}
          />
        ) : null}
        {marketplaceId === "Aliexpress" ? (
          <AliForm
            formRef={formRef}
            onStatusChange={handleButtonProps}
            onSuccess={onSuccess}
            onError={onError}
            seller={seller}
          />
        ) : null}
      </div>
    </Modal>
  );
};

export default EditShopModal;
